const createUKDate = (inputDateStr: string): Date => {
  const [day, month, year] = inputDateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // Months are 0-based in JavaScript
};

export const formatDate = (inputDateStr: string): string => {
  // Use the createUKDate function to parse the input string
  const inputDate = createUKDate(inputDateStr?.split(' ')?.[0] || '');

  if (isNaN(inputDate.getTime())) {
    return 'Invalid Date';
  }

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  const formattedDate = new Intl.DateTimeFormat('en-GB', options)
    ?.format(inputDate)
    ?.replaceAll(' ', ', ')
    ?.replace(',', '');
  return formattedDate;
};

type Procedure = (...args: any[]) => void;

export function debounce<F extends Procedure>(
  func: F,
  delay: number,
): (...args: Parameters<F>) => void {
  let timeoutId: any;

  return function debounced(...args: Parameters<F>): void {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

const uniqueRoutesSubset = ['/examplePath'];
export const hideNavbarOnChildRoute = (pathname: string) =>
  !uniqueRoutesSubset.some((route) => pathname.includes(route));

export function filterEducationArray(educationArray: any): any[] {
  // Create a map to track unique keys and items with a degree
  const seenKeys = new Set<string>();
  const degreeMap = new Map<string, boolean>();

  const newEdArray = educationArray.filter((e: any) => e.school_name && e.start_date && e.degree);
  // Collect unique keys and mark if there is an item with a degree
  for (const item of newEdArray) {
    const key = `${item.school_name.toLowerCase()}-${item.start_date}-${item.end_date}`;

    if (item.degree) {
      degreeMap.set(key, true);
    }
    seenKeys.add(key);
  }

  // Filter the array while maintaining the original order
  const ed = newEdArray.filter((item: any) => {
    const key = `${item.school_name.toLowerCase()}-${item.start_date}-${item.end_date}`;
    const hasDegreeInGroup = degreeMap.get(key) || false;

    // Include the item if it has a degree or if no item in the group has a degree
    return hasDegreeInGroup ? !!item.degree : true;
  });
  return ed.sort((a: any, b: any) => new Date(b.start_date) - new Date(a.start_date));
}

export function filterExperienceArray(experienceArray: any[]): any[] {
  const arrayWithTitles = experienceArray.filter((c: any) => !!c.title);

  return sortExperience(arrayWithTitles).slice(0, 3);
}

export const sortExperience = (data: any[]): any[] => {
  return data.sort((a, b) => {
    // First, prioritize primary (true comes before false)
    if (a.primary === b.primary) {
      // If both have the same primary status, compare by start_date
      return new Date(b.start_date) - new Date(a.start_date);
    }
    // If the primary status is different, prioritize true first
    return b.primary - a.primary;
  });
};

export const formatISODateToReadable = (isoString: string): string => {
  const inputDate = new Date(isoString);

  if (isNaN(inputDate.getTime())) {
    return 'Invalid Date';
  }

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  return new Intl.DateTimeFormat('en-GB', options).format(inputDate);
};

// Utility function to remove duplicates from an array
export const uniqueArray = (array: string[]): string[] => Array.from(new Set([...array]));

// Utility function to split comma-separated input and remove duplicates
export const splitCommaSeparatedInput = (input: string, splitOnInput: boolean): string[] => {
  if (!splitOnInput || !input.includes(',')) {
    return [input.trim()];
  }
  
  const items = input
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);
  
  return uniqueArray(items);
};
